import { useEffect, useState } from "react";
import Header from "../../../component/Header";
import { useDispatch, useSelector } from "react-redux";
import DesktopAppPromotion from "../../../component/DesktopAppPromotion";
import {
  getDeepLinkVerificationCode,
  checkDeepLinkStstus,
  fetchAppInfo,
  getCMSByKey,
} from "../../../redux/Actions";
import { useTranslation } from "react-i18next";
import { strings } from "../../../utils/constants";
import { changeActiveHeader } from "../../../redux/Reducers/GeneralReducer";
import { CircularProgress } from "@mui/material";
import DownloadDesktopApp from "../../../component/DownloadDesktopApp";
import { Toast } from "../../../component/Toast";
import {
  getSource,
  buildDesktopLaunchUrl,
  LAUNCHED_FROM_WEBSITE,
} from "../../../services/source";

const AccessADA = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language } = useSelector((state) => state.generalReducer);
  const [loading, setLoading] = useState(false);
  const [os, setOs] = useState(null);
  const [browserName, setBrowserName] = useState(null);
  const [appOpenAttempted, setAppOpenAttempted] = useState(false);
  const [appVersionChecked, setAppVersionChecked] = useState(false);
  const [hasOldVersion, setHasOldVersion] = useState(false);
  // Banner title/subtitle HTML pulled from the "ada-title" CMS page. Falls back
  // to the static translation if the CMS content isn't available.
  const [adaTitleHtml, setAdaTitleHtml] = useState("");

  // COMMENTED OUT: API calls that require authentication - not needed after login removal
  // const getActivePlan = async () => {
  //   let activePlan = await dispatch(
  //     getActiveSubscriptionPlan({ language: language })
  //   );
  //   if (activePlan.payload?.success) {
  //     if (!activePlan.payload.data?.isFreeUsed) {
  //       dispatch(onFreeTrial(true));
  //     } else {
  //       dispatch(onFreeTrial(false));
  //     }
  //   } else {
  //     dispatch(onFreeTrial(true));
  //   }
  // };

  // const getProfile = async () => {
  //   await dispatch(profileDetails({ language: language, isAdmin: false }));
  // };

  // Define functions first
  function getOperatingSystem(window) {
    let operatingSystem = "Not known";
    if (window.navigator.appVersion.indexOf("Win") !== -1) {
      operatingSystem = "WindowsOS";
    }
    if (window.navigator.appVersion.indexOf("Mac") !== -1) {
      operatingSystem = "MacOS";
    }
    if (window.navigator.appVersion.indexOf("X11") !== -1) {
      operatingSystem = "UNIXOS";
    }
    if (window.navigator.appVersion.indexOf("Linux") !== -1) {
      operatingSystem = "LinuxOS";
    }
    return operatingSystem;
  }

  const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Firefox") > -1) {
      return "Firefox";
    } else if (userAgent.indexOf("Edg") > -1) {
      return "Edge";
    } else if (
      userAgent.indexOf("Chrome") > -1 &&
      userAgent.indexOf("Safari") > -1 &&
      userAgent.indexOf("Edg") === -1
    ) {
      return "Chrome";
    } else if (
      userAgent.indexOf("Safari") > -1 &&
      userAgent.indexOf("Chrome") === -1
    ) {
      return "Safari";
    } else {
      return "Other";
    }
  };

  // Restored deep linking implementation
  const openApp = (verificationId) => {
    try {
      // Pass ONLY the EXISTING website source (e.g. google /
      // http://localhost:3000) through to the desktop app so it can attach the
      // same source to the single access log IT creates. getSource() returns
      // the source already associated with this web session (falling back to
      // the existing "unknown" only when none was ever detected). We do NOT
      // invent or transform the source here.
      //
      // Data ownership: the Website owns the source/launch context only. User
      // and application information (email, subscription, app version, desktop
      // language) are intentionally NOT sent — the desktop app obtains those
      // itself. We only pass `id`, the deep-link verification code that the
      // existing launch/verify mechanism relies on (a launch token, not user
      // information).
      //
      // `launched_from=website` is an explicit marker so the desktop app can
      // clearly record that this launch came FROM the website, independent of
      // the attribution `source` value.
      //
      // Carry the website's current UI language (en/fr) so the desktop app can
      // adopt it when launched from the website. We send it under BOTH `lang`
      // and `language` keys so the desktop handler finds it regardless of which
      // name it reads.
      const lang =
        language === strings.language.FR
          ? strings.language.FR
          : strings.language.EN;
      const deepLinkUrl = buildDesktopLaunchUrl(getSource(), {
        launched_from: LAUNCHED_FROM_WEBSITE,
        lang,
        language: lang,
        id: verificationId,
      });

      // Try to open the deep link
      // This will fail if the app is not installed, which is expected
      window.location.href = deepLinkUrl;

    } catch (error) {
      setLoading(false);
      setAppOpenAttempted(true);
      Toast({
        type: "error",
        title: strings.words.ERROR || "Error",
        message: t(strings.translate.APP_COULD_NOT_OPEN),
      });
    }
  };

  const checkStatus = async () => {
    try {
      const response = await dispatch(checkDeepLinkStstus({ language: "en" }));

      if (response?.payload?.success && response?.payload?.data?.verified === false) {
        setLoading(false);
        setAppOpenAttempted(true);
        Toast({
          type: "warning",
          title: strings.words.WARNING || "Warning",
          message: t(strings.translate.APP_PROTOCOL_NOT_REGISTERED),
        });
      } else if (response?.payload?.success && response?.payload?.data?.verified) {
        setLoading(false);
        Toast({
          type: "success",
          title: strings.words.SUCCESS || "Success",
          message: t(strings.translate.APP_OPENED_SUCCESS),
        });
      } else {
        setLoading(false);
        setAppOpenAttempted(true);
        Toast({
          type: "info",
          title: strings.words.INFO || "Info",
          message: t(strings.translate.APP_STATUS_UNKNOWN),
        });
      }
    } catch (error) {
      setLoading(false);
      setAppOpenAttempted(true);
      Toast({
        type: "error",
        title: strings.words.ERROR || "Error",
        message: t(strings.translate.APP_STATUS_CHECK_ERROR),
      });
    }
  };

  const triggerAppOpenWithOS = async (detectedOS) => {
    setLoading(true);
    setAppOpenAttempted(false);

    // Check if OS supports desktop app
    if (detectedOS === "WindowsOS" || detectedOS === "MacOS") {
      Toast({
        type: "info",
        title: strings.words.INFO || "Info",
        message: t(strings.translate.TRY_OPENING_APP) || "Trying to open desktop application...",
      });

      try {
        // Get verification code from API
        const response = await dispatch(getDeepLinkVerificationCode({ language: "en" }));

        if (response?.payload?.success) {
          const verificationId = response.payload.data;

          Toast({
            type: "info",
            title: strings.words.INFO || "Info",
            message: t(strings.translate.OPENING_DESKTOP_APP),
          });

          // NOTE: We intentionally do NOT create a desktop access log from the
          // website. Data ownership: the desktop application creates the single
          // "desktop" access log itself (with its own user/app info) and uses
          // the website source we hand off via the deep link below. Logging it
          // here too would create a duplicate record.
          openApp(verificationId);

          // Check status after 2 seconds
          setTimeout(checkStatus, 2000);
        } else {
          setLoading(false);
          setAppOpenAttempted(true);
          Toast({
            type: "error",
            title: strings.words.ERROR || "Error",
            message: t(strings.translate.VERIFICATION_CODE_ERROR),
          });
        }
      } catch (error) {
        setLoading(false);
        setAppOpenAttempted(true);
      }
    } else {
      setLoading(false);
      Toast({
        type: "warning",
        title: strings.words.WARNING || "Warning",
        message: t(strings.translate.INSTALL_ONLY_ON_WINDOWS_OR_MAC),
      });
    }
  };

  // Keep the original triggerAppOpen for compatibility (uses state OS)
  // const triggerAppOpen = async () => {
  //   return triggerAppOpenWithOS(os);
  // };

  // Silently check app version in background
  const checkAppVersion = async () => {
    try {
      const response = await dispatch(fetchAppInfo({ language: "en" }));

      if (response?.payload?.success) {
        const appData = response.payload.data;
        const oldVersion = appData?.old_version;
        setHasOldVersion(oldVersion || false);
      }
      // Always mark as checked regardless of result
    } catch (error) {
      // silent — don't show any error to user
    } finally {
      setAppVersionChecked(true);
    }
  };

  useEffect(() => {
    dispatch(changeActiveHeader(strings.navigationHeader.user.ACCESS_ADA));

    const operatingSystem = getOperatingSystem(window);
    setOs(operatingSystem);
    const browser = detectBrowser();
    setBrowserName(browser);

    // Silently check app version in the background — no loading state shown to user
    if (operatingSystem === "WindowsOS" || operatingSystem === "MacOS") {
      checkAppVersion();
    } else {
      setAppVersionChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, t]);

  // Fetch the banner title/subtitle from the "ada-title" CMS page. Re-fetches
  // when the language changes so the correct (content / content_fr) is shown.
  useEffect(() => {
    let cancelled = false;
    const fetchAdaTitle = async () => {
      try {
        const response = await dispatch(
          getCMSByKey({ language, key: strings.cmsPages.ADA_TITLE })
        );
        const data = response?.payload?.data;
        if (!cancelled && response?.payload?.success && data) {
          // ada-title stores plain rich-HTML in content / content_fr.
          const html =
            language === strings.language.FR
              ? data.content_fr || data.content
              : data.content;
          setAdaTitleHtml(html || "");
        }
      } catch (_) {
        // keep the translation fallback on error
      }
    };
    fetchAdaTitle();
    return () => {
      cancelled = true;
    };
  }, [dispatch, language]);

  // Manual trigger for Load App button
  const handleLoadApp = () => {
    if (hasOldVersion) {
      // Show download page if old version
      setAppOpenAttempted(true);
    } else {
      // Try to open the app
      triggerAppOpenWithOS(os);
    }
  };

  // This code will be used when Desktop App will be available on Apple store and Microsoft store.

  // COMMENTED OUT: Status checking requires authentication - not needed after login removal
  // const checkStatus = async () => {
  //   const response = await dispatch(
  //     checkDeepLinkStstus({ language: language })
  //   );
  //   if (
  //     response?.payload?.success &&
  //     response?.payload?.data?.verified === false
  //   ) {
  //     setLoading(false);
  //     Toast({
  //       type: "error",
  //       title: strings.words.ERROR,
  //       message: t(strings.translate.DESKTOP_APP_NOT_OPEN),
  //     });
  //   } else if (
  //     response?.payload?.success &&
  //     response?.payload?.data?.verified &&
  //     !response?.payload?.data?.old_version
  //   ) {
  //     setLoading(false);
  //     navigation(-1);
  //     } else {
  //     setLoading(false);
  //   }
  // };
  // COMMENTED OUT: App opening functionality requires profile data - not available after login removal
  // const openApp = (id) => {
  //   window.open(
  //     "formdepot://id=" + id + "&email=" + profile?.data.email,
  //     "_self"
  //   );
  // };
  // COMMENTED OUT: Deep link functionality requires authentication - not needed after login removal
  // const triggerAppOpen = async () => {
  //   setLoading(true);
  //   const response = await dispatch(
  //     getDeepLinkVerificationCode({ language: language })
  //   );
  //   if (response?.payload?.success) {
  //     const appInfoResponse = await dispatch(
  //       fetchAppInfo({ language: language })
  //     );
  //     if (appInfoResponse?.payload?.success) {
  //       openApp(response?.payload?.data);
  //       setTimeout(checkStatus, 10000);
  //     } else {
  //       setLoading(false);
  //     }
  //   } else {
  //     setLoading(false);
  //   }
  // };

  // const downloadFile = (url) => {
  //   // const url1 = "https://formdepot.s3.ca-central-1.amazonaws.com/1688467678642.71249a3b-d8a2-40be-a9fb-b5067365faa4.pdf"
  //   const url1 =
  //     "https://tourism.gov.in/sites/default/files/2019-04/dummy-pdf_2.pdf";
  //   const fileName = url1.split("/").pop();
  //   const aTag = document.createElement("a");
  //   aTag.href = url1;
  //   aTag.setAttribute("download", fileName);
  //   document.body.appendChild(aTag);
  //   aTag.click();
  //   aTag.remove();
  // };
  // Simplified: Always show promotional page






  return (
    <div className="ada-page-wrapper">
      <style>{`
        .ada-page-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: #fff;
        }
      `}</style>
      <Header />
      <section className="subplan-banner bg--primary">
        <div className="container">
          {adaTitleHtml ? (
            <h1
              className="font-lato_bold font-26 fg-white text-center py-5 m-0"
              style={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={{ __html: adaTitleHtml }}
            />
          ) : (
            <h1 className="font-lato_bold font-26 fg-white text-center py-5 m-0" style={{ whiteSpace: "pre-line" }}>
              {t(strings.translate.AUTOFILL_FORMS_FEATURE)}
            </h1>
          )}
        </div>
      </section>

      {loading ? (
        <section className="billing-sec start-trial mt-2 pt-2 mt-md-4 pt-md-4">
          <div className="container">
            <div className="d-flex flex-column justify-content-center align-items-center">
              <div className="alert alert-info mb-4 text-center" role="alert">
                <strong>{t(strings.words.TIP)}:</strong>{" "}
                {t(strings.translate.DOWNLOAD_APP_TIP_PART1)}
                <strong>
                  {" "}
                  "{t(strings.translate.DOWNLOAD_APP_TIP_BOLD_PART1)}"{" "}
                </strong>
                {t(strings.translate.DOWNLOAD_APP_TIP_PART2)}
                <strong>
                  "{t(strings.translate.DOWNLOAD_APP_TIP_BOLD_PART2)}"
                </strong>
                . {t(strings.translate.DOWNLOAD_APP_TIP_PART3)}
              </div>
              <div className="mt-5">
                <CircularProgress color={"success"} size={50} />
              </div>
              <div className="my-5 fs-4">
                {t(strings.translate.OPENING_APP_LOADING)}
              </div>
              <div className="alert alert-success mt-5" role="alert">
                <div>
                  <strong>{t(strings.words.NOTE)}: </strong>
                  {t(strings.translate.DOWNLOAD_APP_NOTE_PART1)}
                </div>
                <div>
                  {t(strings.translate.DOWNLOAD_APP_NOTE_PART2)}{" "}
                  <span>
                    {browserName === "Chrome" && (
                      <a
                        href="https://support.google.com/chrome/answer/95472?hl=en&co=GENIE.Platform%3DDesktop"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Chrome
                      </a>
                    )}
                    {browserName === "Edge" && (
                      <a
                        href="https://support.microsoft.com/en-us/microsoft-edge/block-pop-ups-in-microsoft-edge-1d8ba4f8-f385-9a0b-e944-aa47339b6bb5"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Edge
                      </a>
                    )}
                    {browserName === "Safari" && (
                      <a
                        href="https://support.apple.com/en-ca/102524"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Safari
                      </a>
                    )}
                    {browserName === "Firefox" && (
                      <a
                        href="https://support.mozilla.org/en-US/kb/pop-blocker-settings-exceptions-troubleshooting"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Firefox
                      </a>
                    )}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </section>
      ) : (hasOldVersion || appOpenAttempted) && !loading ? (
        <DownloadDesktopApp os={os} onOpenApp={() => triggerAppOpenWithOS(os)} />
      ) : (
        <DesktopAppPromotion onLoadApp={handleLoadApp} />
      )}
    </div>
  );
};

export default AccessADA;
